import { useMutation } from '@tanstack/react-query';

import { useToastActions } from 'stores/toastStore';

type UseMutateWithToastProps = {
  mutationFn: (data: any) => Promise<any>;
  onSuccess?: (data?: any) => void;
  onError?: (error?: unknown) => void;
  onSettled?: () => void;
  // Turns a failure into the toast text. Returning undefined falls back to the generic message, so a
  // caller can surface the rejections it knows how to read (a 422's validation reasons, say) and
  // leave a network blip or a 500 shrugging as before.
  errorToast?: (error: unknown) => string | undefined;
};

const useMutateWithToast = ({ mutationFn, onSuccess, onError, onSettled, errorToast }: UseMutateWithToastProps) => {
  const { addToast } = useToastActions();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError: (error) => {
      addToast({ text: errorToast?.(error) ?? 'Something went wrong. Please try again' });
      onError?.(error);
    },

    onSettled: () => {
      onSettled?.();
    },
  });

  // `mutateAsync` alongside `mutate` for callers that must SEQUENCE writes — awaiting one before
  // starting the next. It rejects on failure, so an awaiting caller has to catch; the toast is
  // raised either way.
  return { mutate, mutateAsync, isPending };
};

export default useMutateWithToast;
